import { t } from '../../i18n';
import { MoneyTotals } from '../../types/customer';
import {
  AgingBucket,
  AgingReport,
  AgreementVarianceReport,
  CategorySummaryReport,
  CustomerBalancesReport,
  MonthlySummaryReport,
  UnallocatedPaymentsReport,
  YearlyMonthlyReport,
} from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import type { ExcelCell } from '../../utils/exportExcelXml';
import { escapeHtml } from '../../utils/printDocument';
import {
  dateCell,
  excelSubtotal,
  htmlDataRow,
  moneyCell,
} from '../customers/exportCustomerShared';
import {
  downloadReportExcel,
  printReportPdf,
  reportHeaderRowIndex,
  reportSummaryCard,
  wrapSummaryHtml,
  type ReportMetaLine,
} from './exportReportShared';

export type ExportFormat = 'excel' | 'pdf';
export type AgingViewMode = 'total' | 'invoice' | 'cash';

const AGING_BUCKETS: AgingBucket[] = [
  'notDue',
  'd1_30',
  'd31_60',
  'd61_90',
  'd90plus',
];

function dash(value: string | null | undefined): string {
  return value && value.trim() ? value : '—';
}

function yesNo(value: boolean): string {
  return value ? t().common.yes : t().common.no;
}

function pickAmount(totals: MoneyTotals, view: AgingViewMode): string {
  if (view === 'invoice') return totals.invoice;
  if (view === 'cash') return totals.cash;
  return totals.total;
}

function htmlMoneyFooter(
  label: string,
  amounts: Array<string | number>,
  labelColSpan = 1,
  trailingEmpty = 0,
): string {
  const labelCell =
    labelColSpan > 1
      ? `<td colspan="${labelColSpan}">${escapeHtml(label)}</td>`
      : `<td>${escapeHtml(label)}</td>`;
  const money = amounts
    .map((amount) => `<td class="num">${escapeHtml(formatMoney(amount))}</td>`)
    .join('');
  const empty = Array.from({ length: trailingEmpty }, () => '<td></td>').join('');
  return `<tr>${labelCell}${money}${empty}</tr>`;
}

function subtotalCells(
  firstRow: number,
  lastRow: number,
  columns: number[],
): ExcelCell[] {
  return columns.map((column) => excelSubtotal(column, firstRow, lastRow));
}

function moneyCells(values: Array<string | number>, style = 'MoneyBold'): ExcelCell[] {
  return values.map((value) => moneyCell(value, style));
}

export function exportMonthlySummary(input: {
  report: MonthlySummaryReport;
  charges: MonthlySummaryReport['charges'];
  payments: MonthlySummaryReport['payments'];
  officeName?: string;
  format: ExportFormat;
}): void {
  const { report, charges, payments, officeName, format } = input;
  const labels = t().reports;
  const title = t().nav.monthlySummary;
  const monthName = t().common.months[report.month - 1] ?? String(report.month);
  const meta: ReportMetaLine[] = [
    { label: labels.month, value: monthName },
    { label: labels.year, value: String(report.year) },
    {
      label: t().nav.office,
      value: officeName ?? t().customers.filterAll,
    },
  ];
  const filename = `miniaia-synopsi-${report.year}-${String(report.month).padStart(2, '0')}.xls`;

  const chargeHeaders = [
    t().common.date,
    t().common.customer,
    t().invoices.invoiceNo,
    t().invoices.category,
    t().financial.invoice,
    t().financial.cash,
    t().common.total,
  ];
  const paymentHeaders = [
    t().common.date,
    t().common.customer,
    t().invoices.invoiceNo,
    t().financial.invoice,
    t().financial.cash,
    t().common.total,
  ];
  const chargeNumeric = (index: number) => index >= 4;
  const paymentNumeric = (index: number) => index >= 3;

  if (format === 'excel') {
    downloadReportExcel({
      filename,
      title,
      meta,
      columnCount: 7,
      columnWidths: [12, 28, 14, 18, 14, 14, 14],
      sections: [
        {
          title: labels.monthCharges,
          headers: chargeHeaders,
          isNumeric: chargeNumeric,
          dataRows: charges.map((row) => [
            dateCell(row.date),
            { type: 'string', value: row.customer.name },
            { type: 'string', value: row.invoiceNo },
            { type: 'string', value: row.category?.name ?? '—' },
            moneyCell(row.totals.invoice),
            moneyCell(row.totals.cash),
            moneyCell(row.totals.total),
          ]),
          totalRow: [
            {
              type: 'string',
              value: t().common.total,
              style: 'Label',
              mergeAcross: 3,
            },
            ...moneyCells([
              report.summary.charges.invoice,
              report.summary.charges.cash,
              report.summary.charges.total,
            ]),
          ],
        },
        {
          title: labels.monthPayments,
          headers: paymentHeaders,
          isNumeric: paymentNumeric,
          dataRows: payments.map((row) => [
            dateCell(row.date),
            { type: 'string', value: row.customer.name },
            { type: 'string', value: row.invoice?.invoiceNo ?? '—' },
            moneyCell(row.totals.invoice),
            moneyCell(row.totals.cash),
            moneyCell(row.totals.total),
          ]),
          totalRow: [
            {
              type: 'string',
              value: t().common.total,
              style: 'Label',
              mergeAcross: 2,
            },
            ...moneyCells([
              report.summary.payments.invoice,
              report.summary.payments.cash,
              report.summary.payments.total,
            ]),
          ],
        },
      ],
    });
    return;
  }

  printReportPdf({
    title,
    meta,
    firstPageRows: 12,
    summaryHtml: wrapSummaryHtml(
      reportSummaryCard(
        labels.chargeTotal,
        report.summary.charges.invoice,
        report.summary.charges.cash,
        report.summary.charges.total,
        labels.chargeInvoice,
        labels.chargeCash,
      ),
      reportSummaryCard(
        labels.paymentTotal,
        report.summary.payments.invoice,
        report.summary.payments.cash,
        report.summary.payments.total,
        labels.paymentInvoice,
        labels.paymentCash,
      ),
    ),
    sections: [
      {
        title: labels.monthCharges,
        headers: chargeHeaders,
        isNumeric: chargeNumeric,
        rowHtml: charges.map((row) =>
          htmlDataRow(
            [
              formatDate(row.date),
              row.customer.name,
              row.invoiceNo,
              row.category?.name ?? '—',
              formatMoney(row.totals.invoice),
              formatMoney(row.totals.cash),
              formatMoney(row.totals.total),
            ],
            chargeNumeric,
          ),
        ),
        totalsRowHtml: htmlMoneyFooter(t().common.total, [
          report.summary.charges.invoice,
          report.summary.charges.cash,
          report.summary.charges.total,
        ], 4),
      },
      {
        title: labels.monthPayments,
        headers: paymentHeaders,
        isNumeric: paymentNumeric,
        rowHtml: payments.map((row) =>
          htmlDataRow(
            [
              formatDate(row.date),
              row.customer.name,
              row.invoice?.invoiceNo ?? '—',
              formatMoney(row.totals.invoice),
              formatMoney(row.totals.cash),
              formatMoney(row.totals.total),
            ],
            paymentNumeric,
          ),
        ),
        totalsRowHtml: htmlMoneyFooter(t().common.total, [
          report.summary.payments.invoice,
          report.summary.payments.cash,
          report.summary.payments.total,
        ], 3),
      },
    ],
  });
}

export function exportYearlyMonthly(input: {
  report: YearlyMonthlyReport;
  officeName?: string;
  format: ExportFormat;
}): void {
  const { report, officeName, format } = input;
  const labels = t().reports;
  const months = t().common.months;
  const title = t().nav.yearlyMonthly;
  const meta: ReportMetaLine[] = [
    { label: labels.year, value: String(report.year) },
    {
      label: t().nav.office,
      value: officeName ?? t().customers.filterAll,
    },
  ];
  const headers = [
    labels.month,
    labels.seriesInvoiceCharges,
    labels.seriesCashCharges,
    labels.chargeTotal,
    labels.paymentInvoice,
    labels.paymentCash,
    labels.paymentTotal,
  ];
  const isNumeric = (index: number) => index >= 1;
  const headerRow = reportHeaderRowIndex(meta.length);
  const firstData = headerRow + 1;
  const lastData = headerRow + report.months.length;

  if (format === 'excel') {
    downloadReportExcel({
      filename: `etisia-analysi-${report.year}.xls`,
      title,
      meta,
      columnCount: 7,
      columnWidths: [16, 16, 14, 14, 14, 14, 14],
      sections: [
        {
          headers,
          isNumeric,
          dataRows: report.months.map((row) => [
            { type: 'string', value: months[row.month - 1] ?? String(row.month) },
            moneyCell(row.charges.invoice),
            moneyCell(row.charges.cash),
            moneyCell(row.charges.total),
            moneyCell(row.payments.invoice),
            moneyCell(row.payments.cash),
            moneyCell(row.payments.total),
          ]),
          totalRow: [
            { type: 'string', value: labels.yearTotal, style: 'Label' },
            ...subtotalCells(firstData, lastData, [2, 3, 4, 5, 6, 7]),
          ],
        },
      ],
    });
    return;
  }

  printReportPdf({
    title,
    meta,
    firstPageRows: 14,
    summaryHtml: wrapSummaryHtml(
      reportSummaryCard(
        labels.chargeTotal,
        report.totals.charges.invoice,
        report.totals.charges.cash,
        report.totals.charges.total,
        labels.seriesInvoiceCharges,
        labels.seriesCashCharges,
      ),
      reportSummaryCard(
        labels.paymentTotal,
        report.totals.payments.invoice,
        report.totals.payments.cash,
        report.totals.payments.total,
        labels.paymentInvoice,
        labels.paymentCash,
      ),
    ),
    sections: [
      {
        headers,
        isNumeric,
        rowHtml: report.months.map((row) =>
          htmlDataRow(
            [
              months[row.month - 1] ?? String(row.month),
              formatMoney(row.charges.invoice),
              formatMoney(row.charges.cash),
              formatMoney(row.charges.total),
              formatMoney(row.payments.invoice),
              formatMoney(row.payments.cash),
              formatMoney(row.payments.total),
            ],
            isNumeric,
          ),
        ),
        totalsRowHtml: htmlMoneyFooter(labels.yearTotal, [
          report.totals.charges.invoice,
          report.totals.charges.cash,
          report.totals.charges.total,
          report.totals.payments.invoice,
          report.totals.payments.cash,
          report.totals.payments.total,
        ]),
      },
    ],
  });
}

function daysSincePayment(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const startToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startPayment = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  return Math.max(
    0,
    Math.round((startToday.getTime() - startPayment.getTime()) / 86400000),
  );
}

export function exportCustomerBalances(input: {
  report: CustomerBalancesReport;
  rows: CustomerBalancesReport['data'];
  meta: ReportMetaLine[];
  format: ExportFormat;
}): void {
  const { report, rows, meta, format } = input;
  const labels = t().reports;
  const title = t().nav.customerBalances;
  const headers = [
    t().common.customer,
    t().nav.office,
    t().customers.assignedUser,
    labels.chargesInvoice,
    labels.paymentsInvoiceCol,
    labels.outstandingInvoice,
    labels.chargesCash,
    labels.paymentsCashCol,
    labels.outstandingCash,
    labels.outstandingTotal,
    t().dashboard.lastPaymentDate,
    t().dashboard.daysSinceLastPayment,
    t().dashboard.openInvoices,
  ];
  const isNumeric = (index: number) =>
    (index >= 3 && index <= 9) || index === 11 || index === 12;
  const headerRow = reportHeaderRowIndex(meta.length);
  const firstData = headerRow + 1;
  const lastData = headerRow + rows.length;
  const openInvoiceTotal = rows.reduce(
    (sum, row) => sum + row.openInvoiceCount,
    0,
  );

  if (format === 'excel') {
    downloadReportExcel({
      filename: 'ypolipa-pelaton.xls',
      title,
      meta,
      columnCount: 13,
      columnWidths: [28, 18, 18, 14, 14, 14, 14, 14, 14, 14, 16, 14, 12],
      sections: [
        {
          headers,
          isNumeric,
          dataRows: rows.map((row) => {
            const days = daysSincePayment(row.lastPaymentDate);
            return [
              { type: 'string', value: row.customerName },
              { type: 'string', value: row.officeName ?? '—' },
              { type: 'string', value: row.assignedUserName ?? '—' },
              moneyCell(row.charges.invoice),
              moneyCell(row.payments.invoice),
              moneyCell(row.outstanding.invoice),
              moneyCell(row.charges.cash),
              moneyCell(row.payments.cash),
              moneyCell(row.outstanding.cash),
              moneyCell(row.outstanding.total),
              dateCell(row.lastPaymentDate),
              days == null
                ? { type: 'empty' }
                : { type: 'number', value: days },
              { type: 'number', value: row.openInvoiceCount },
            ];
          }),
          totalRow: [
            {
              type: 'string',
              value: t().common.total,
              style: 'Label',
              mergeAcross: 2,
            },
            ...subtotalCells(firstData, lastData, [4, 5, 6, 7, 8, 9, 10]),
            { type: 'empty' },
            { type: 'empty' },
            ...subtotalCells(firstData, lastData, [13]),
          ],
        },
      ],
    });
    return;
  }

  const moneyTotals = [
    report.totals.charges.invoice,
    report.totals.payments.invoice,
    report.totals.outstanding.invoice,
    report.totals.charges.cash,
    report.totals.payments.cash,
    report.totals.outstanding.cash,
    report.totals.outstanding.total,
  ];
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric,
        rowHtml: rows.map((row) =>
          htmlDataRow(
            [
              row.customerName,
              row.officeName ?? '—',
              row.assignedUserName ?? '—',
              formatMoney(row.charges.invoice),
              formatMoney(row.payments.invoice),
              formatMoney(row.outstanding.invoice),
              formatMoney(row.charges.cash),
              formatMoney(row.payments.cash),
              formatMoney(row.outstanding.cash),
              formatMoney(row.outstanding.total),
              row.lastPaymentDate ? formatDate(row.lastPaymentDate) : '—',
              daysSincePayment(row.lastPaymentDate)?.toString() ?? '—',
              String(row.openInvoiceCount),
            ],
            isNumeric,
          ),
        ),
        totalsRowHtml: `<tr><td colspan="3">${escapeHtml(t().common.total)}</td>${moneyTotals
          .map(
            (amount) =>
              `<td class="num">${escapeHtml(formatMoney(amount))}</td>`,
          )
          .join('')}<td></td><td></td><td class="num">${openInvoiceTotal}</td></tr>`,
      },
    ],
  });
}

export function exportAgingReport(input: {
  report: AgingReport;
  rows: AgingReport['data'];
  view: AgingViewMode;
  officeName?: string;
  assigneeName?: string;
  format: ExportFormat;
}): void {
  const { report, rows, view, officeName, assigneeName, format } = input;
  const labels = t().reports;
  const title = t().nav.aging;
  const viewLabel =
    view === 'invoice'
      ? labels.viewInvoice
      : view === 'cash'
        ? labels.viewCash
        : labels.viewTotal;
  const meta: ReportMetaLine[] = [
    { label: labels.viewMode, value: viewLabel },
    { label: t().common.date, value: formatDate(report.asOf) },
    {
      label: t().nav.office,
      value: officeName ?? t().customers.filterAll,
    },
    {
      label: t().customers.assignedUser,
      value: assigneeName ?? t().customers.filterAll,
    },
  ];
  const headers = [
    t().common.customer,
    t().nav.office,
    t().customers.assignedUser,
    ...AGING_BUCKETS.map((bucket) => labels.buckets[bucket]),
    t().common.total,
    t().dashboard.lastPaymentDate,
    t().dashboard.daysSinceLastPayment,
    t().dashboard.openInvoices,
  ];
  const isNumeric = (index: number) =>
    (index >= 3 && index <= 8) || index === 10 || index === 11;
  const headerRow = reportHeaderRowIndex(meta.length);
  const firstData = headerRow + 1;
  const lastData = headerRow + rows.length;
  const openInvoiceTotal = rows.reduce(
    (sum, row) => sum + row.openInvoiceCount,
    0,
  );

  if (format === 'excel') {
    downloadReportExcel({
      filename: 'lixiprothesmes-ofeiles.xls',
      title,
      meta,
      columnCount: 12,
      columnWidths: [28, 18, 18, 14, 14, 14, 14, 14, 14, 16, 14, 12],
      sections: [
        {
          headers,
          isNumeric,
          dataRows: rows.map((row) => {
            const days = daysSincePayment(row.lastPaymentDate);
            return [
              { type: 'string', value: row.customerName },
              { type: 'string', value: row.officeName ?? '—' },
              { type: 'string', value: row.assignedUserName ?? '—' },
              moneyCell(pickAmount(row.buckets.notDue, view)),
              moneyCell(pickAmount(row.buckets.d1_30, view)),
              moneyCell(pickAmount(row.buckets.d31_60, view)),
              moneyCell(pickAmount(row.buckets.d61_90, view)),
              moneyCell(pickAmount(row.buckets.d90plus, view)),
              moneyCell(pickAmount(row.total, view)),
              dateCell(row.lastPaymentDate),
              days == null
                ? { type: 'empty' }
                : { type: 'number', value: days },
              { type: 'number', value: row.openInvoiceCount },
            ];
          }),
          totalRow: [
            {
              type: 'string',
              value: t().common.total,
              style: 'Label',
              mergeAcross: 2,
            },
            ...subtotalCells(firstData, lastData, [4, 5, 6, 7, 8, 9]),
            { type: 'empty' },
            { type: 'empty' },
            ...subtotalCells(firstData, lastData, [12]),
          ],
        },
      ],
    });
    return;
  }

  const moneyTotals = [
    pickAmount(report.totals.buckets.notDue, view),
    pickAmount(report.totals.buckets.d1_30, view),
    pickAmount(report.totals.buckets.d31_60, view),
    pickAmount(report.totals.buckets.d61_90, view),
    pickAmount(report.totals.buckets.d90plus, view),
    pickAmount(report.totals.total, view),
  ];
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric,
        rowHtml: rows.map((row) =>
          htmlDataRow(
            [
              row.customerName,
              row.officeName ?? '—',
              row.assignedUserName ?? '—',
              formatMoney(pickAmount(row.buckets.notDue, view)),
              formatMoney(pickAmount(row.buckets.d1_30, view)),
              formatMoney(pickAmount(row.buckets.d31_60, view)),
              formatMoney(pickAmount(row.buckets.d61_90, view)),
              formatMoney(pickAmount(row.buckets.d90plus, view)),
              formatMoney(pickAmount(row.total, view)),
              row.lastPaymentDate ? formatDate(row.lastPaymentDate) : '—',
              daysSincePayment(row.lastPaymentDate)?.toString() ?? '—',
              String(row.openInvoiceCount),
            ],
            isNumeric,
          ),
        ),
        totalsRowHtml: `<tr><td colspan="3">${escapeHtml(t().common.total)}</td>${moneyTotals
          .map(
            (amount) =>
              `<td class="num">${escapeHtml(formatMoney(amount))}</td>`,
          )
          .join('')}<td></td><td></td><td class="num">${openInvoiceTotal}</td></tr>`,
      },
    ],
  });
}

export function exportCategorySummary(input: {
  report: CategorySummaryReport;
  rows: CategorySummaryReport['data'];
  meta: ReportMetaLine[];
  format: ExportFormat;
}): void {
  const { report, rows, meta, format } = input;
  const title = t().nav.categorySummary;
  const headers = [
    t().invoices.category,
    t().financial.invoice,
    t().financial.cash,
    t().common.total,
  ];
  const isNumeric = (index: number) => index >= 1;
  const headerRow = reportHeaderRowIndex(meta.length);
  const firstData = headerRow + 1;
  const lastData = headerRow + rows.length;

  if (format === 'excel') {
    downloadReportExcel({
      filename: 'analysi-ana-katigoria.xls',
      title,
      meta,
      columnCount: 4,
      columnWidths: [32, 16, 16, 16],
      sections: [
        {
          headers,
          isNumeric,
          dataRows: rows.map((row) => [
            { type: 'string', value: row.categoryName },
            moneyCell(row.totals.invoice),
            moneyCell(row.totals.cash),
            moneyCell(row.totals.total),
          ]),
          totalRow: [
            { type: 'string', value: t().common.total, style: 'Label' },
            ...subtotalCells(firstData, lastData, [2, 3, 4]),
          ],
        },
      ],
    });
    return;
  }

  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric,
        rowHtml: rows.map((row) =>
          htmlDataRow(
            [
              row.categoryName,
              formatMoney(row.totals.invoice),
              formatMoney(row.totals.cash),
              formatMoney(row.totals.total),
            ],
            isNumeric,
          ),
        ),
        totalsRowHtml: htmlMoneyFooter(t().common.total, [
          report.totals.invoice,
          report.totals.cash,
          report.totals.total,
        ]),
      },
    ],
  });
}

export function exportUnallocatedPayments(input: {
  report: UnallocatedPaymentsReport;
  rows: UnallocatedPaymentsReport['data'];
  meta: ReportMetaLine[];
  format: ExportFormat;
}): void {
  const { report, rows, meta, format } = input;
  const labels = t().reports;
  const title = t().nav.unallocatedPayments;
  const headers = [
    t().common.date,
    t().common.customer,
    t().payments.description,
    labels.paymentAmount,
    labels.allocatedAmount,
    t().financial.invoice,
    t().financial.cash,
    labels.unallocatedAmount,
    labels.allocationCount,
  ];
  const isNumeric = (index: number) => index >= 3 && index <= 7;
  const headerRow = reportHeaderRowIndex(meta.length);
  const firstData = headerRow + 1;
  const lastData = headerRow + rows.length;
  const statusOf = (row: UnallocatedPaymentsReport['data'][number]) =>
    row.isFullyUnallocated
      ? labels.fullyUnallocated
      : String(row.allocationCount);

  if (format === 'excel') {
    downloadReportExcel({
      filename: 'mi-katanemimenes-pliromes.xls',
      title,
      meta,
      columnCount: 9,
      columnWidths: [12, 26, 28, 16, 16, 14, 14, 18, 16],
      sections: [
        {
          headers,
          isNumeric,
          dataRows: rows.map((row) => [
            dateCell(row.paymentDate),
            { type: 'string', value: row.customer.name },
            {
              type: 'string',
              value: row.description || `${t().payments.title} #${row.id}`,
            },
            moneyCell(row.totals.total),
            moneyCell(row.allocated.total),
            moneyCell(row.unallocated.invoice),
            moneyCell(row.unallocated.cash),
            moneyCell(row.unallocated.total),
            { type: 'string', value: statusOf(row) },
          ]),
          totalRow: [
            {
              type: 'string',
              value: t().common.total,
              style: 'Label',
              mergeAcross: 2,
            },
            ...subtotalCells(firstData, lastData, [4, 5, 6, 7, 8]),
            { type: 'empty', style: 'Label' },
          ],
        },
      ],
    });
    return;
  }

  printReportPdf({
    title,
    meta,
    firstPageRows: 12,
    summaryHtml: wrapSummaryHtml(
      reportSummaryCard(
        labels.paymentAmount,
        report.totals.payment.invoice,
        report.totals.payment.cash,
        report.totals.payment.total,
      ),
      reportSummaryCard(
        labels.allocatedAmount,
        report.totals.allocated.invoice,
        report.totals.allocated.cash,
        report.totals.allocated.total,
      ),
      reportSummaryCard(
        labels.unallocatedAmount,
        report.totals.unallocated.invoice,
        report.totals.unallocated.cash,
        report.totals.unallocated.total,
      ),
    ),
    sections: [
      {
        headers,
        isNumeric,
        rowHtml: rows.map((row) =>
          htmlDataRow(
            [
              formatDate(row.paymentDate),
              row.customer.name,
              row.description || `${t().payments.title} #${row.id}`,
              formatMoney(row.totals.total),
              formatMoney(row.allocated.total),
              formatMoney(row.unallocated.invoice),
              formatMoney(row.unallocated.cash),
              formatMoney(row.unallocated.total),
              statusOf(row),
            ],
            isNumeric,
          ),
        ),
        totalsRowHtml: htmlMoneyFooter(
          t().common.total,
          [
            report.totals.payment.total,
            report.totals.allocated.total,
            report.totals.unallocated.invoice,
            report.totals.unallocated.cash,
            report.totals.unallocated.total,
          ],
          3,
          1,
        ),
      },
    ],
  });
}

export function exportAgreementVariance(input: {
  report: AgreementVarianceReport;
  rows: AgreementVarianceReport['data'];
  meta: ReportMetaLine[];
  format: ExportFormat;
}): void {
  const { report, rows, meta, format } = input;
  const labels = t().reports;
  const title = t().nav.agreementVariance;
  const headers = [
    t().common.customer,
    labels.agreementMonthly,
    labels.expectedBilling,
    labels.actualBilling,
    t().financial.invoice,
    t().financial.cash,
    labels.variance,
  ];
  const isNumeric = (index: number) => index >= 1;
  const headerRow = reportHeaderRowIndex(meta.length);
  const firstData = headerRow + 1;
  const lastData = headerRow + rows.length;

  if (format === 'excel') {
    downloadReportExcel({
      filename: `apoklisi-symfonias-${report.year}${
        report.month ? `-${String(report.month).padStart(2, '0')}` : ''
      }.xls`,
      title,
      meta,
      columnCount: 7,
      columnWidths: [28, 16, 16, 18, 14, 14, 14],
      sections: [
        {
          headers,
          isNumeric,
          dataRows: rows.map((row) => [
            { type: 'string', value: row.customerName },
            moneyCell(row.agreement.total),
            moneyCell(row.expected.total),
            moneyCell(row.billed.total),
            moneyCell(row.variance.invoice),
            moneyCell(row.variance.cash),
            moneyCell(row.variance.total),
          ]),
          totalRow: [
            { type: 'string', value: t().common.total, style: 'Label' },
            ...subtotalCells(firstData, lastData, [2, 3, 4, 5, 6, 7]),
          ],
        },
      ],
    });
    return;
  }

  printReportPdf({
    title,
    meta,
    firstPageRows: 12,
    summaryHtml: wrapSummaryHtml(
      reportSummaryCard(
        `${labels.expectedBilling} (${report.periodMonths} ${labels.periodMonths.toLowerCase()})`,
        report.totals.expected.invoice,
        report.totals.expected.cash,
        report.totals.expected.total,
      ),
      reportSummaryCard(
        labels.actualBilling,
        report.totals.billed.invoice,
        report.totals.billed.cash,
        report.totals.billed.total,
      ),
      reportSummaryCard(
        labels.variance,
        report.totals.variance.invoice,
        report.totals.variance.cash,
        report.totals.variance.total,
      ),
    ),
    sections: [
      {
        headers,
        isNumeric,
        rowHtml: rows.map((row) =>
          htmlDataRow(
            [
              row.customerName,
              formatMoney(row.agreement.total),
              formatMoney(row.expected.total),
              formatMoney(row.billed.total),
              formatMoney(row.variance.invoice),
              formatMoney(row.variance.cash),
              formatMoney(row.variance.total),
            ],
            isNumeric,
          ),
        ),
        totalsRowHtml: htmlMoneyFooter(t().common.total, [
          report.totals.agreement.total,
          report.totals.expected.total,
          report.totals.billed.total,
          report.totals.variance.invoice,
          report.totals.variance.cash,
          report.totals.variance.total,
        ]),
      },
    ],
  });
}

export type { ReportMetaLine } from './exportReportShared';

export { dash, yesNo };
